import { z } from 'zod';
import { uuidSchema, emailSchema, shortString, mediumString } from './validation.js';
/**
 * User/Profile Schemas
 *
 * Shared validation schemas for user management operations.
 * All users are organization-scoped with role-based access control.
 */
/**
 * User roles in the system
 */
export const userRoleSchema = z.enum([
    'Super Admin',
    'Admin',
    'Warehouse Coordinator',
    'Warehouse Staff',
    'Viewer',
    'Picker',
    'Printer',
    'Catcher',
    'Packer',
    'CX',
    // BLU-1335: grantable by org admins, at floors the org runs itself only.
    'DTF Printer',
]);
/**
 * Per-user sidebar page allowlist for Viewer role. Array of page IDs from the
 * frontend page-registry. Ignored for non-Viewer roles. Optional on every
 * mutation — when omitted, the existing membership row's value is preserved.
 */
const viewerVisiblePagesSchema = z.array(shortString.min(1).max(64)).max(64).optional();
/**
 * Location assignment for a user.
 *
 * A saved user must always retain at least one location: with zero locations,
 * a non-Admin/non-Super-Admin can no longer sign in (the frontend login gate in
 * AuthContext rejects `locations.length === 0`), and even a Super Admin was
 * locked out before that gate was widened. The field stays `.optional()` so a
 * partial update that OMITS it preserves the existing assignments — but an
 * explicit empty array is rejected rather than silently wiping every row.
 */
const locationIdsSchema = z
    .array(uuidSchema)
    .min(1, 'Select at least one location')
    .optional();
/**
 * Create a new user (Admin only)
 */
export const createUserSchema = z.object({
    email: emailSchema,
    password: mediumString.min(8, 'Password must be at least 8 characters'),
    firstName: shortString.min(1, 'First name is required'),
    lastName: shortString.min(1, 'Last name is required'),
    role: userRoleSchema.default('Warehouse Staff'),
    locationIds: locationIdsSchema,
    viewerVisiblePages: viewerVisiblePagesSchema,
    sendInviteEmail: z.boolean().default(true).optional(),
});
export const updateUserSchema = z.object({
    userId: uuidSchema,
    firstName: shortString.min(1).optional(),
    lastName: shortString.min(1).optional(),
    role: userRoleSchema.optional(),
    locationIds: locationIdsSchema,
    viewerVisiblePages: viewerVisiblePagesSchema,
});
/**
 * Get users with filters
 */
export const getUsersSchema = z.object({
    role: userRoleSchema.optional(),
    locationId: uuidSchema.optional(),
    isActive: z.boolean().optional(),
    includeInactive: z.boolean().default(false).optional(),
});
/**
 * Toggle user active status
 */
export const toggleUserStatusSchema = z.object({
    userId: uuidSchema,
    isActive: z.boolean(),
    reason: mediumString.optional(),
});
export const deleteUserSchema = z.object({
    userId: uuidSchema,
});
/**
 * Reset user password (Admin only)
 */
export const resetPasswordSchema = z.object({
    userId: uuidSchema,
    temporaryPassword: mediumString.min(8, 'Password must be at least 8 characters'),
});
/**
 * Invite a new user via email (Admin only)
 */
export const inviteUserSchema = z.object({
    email: emailSchema,
    firstName: shortString.min(1, 'First name is required'),
    lastName: shortString.min(1, 'Last name is required'),
    role: userRoleSchema.default('Warehouse Staff'),
    locationIds: locationIdsSchema,
    viewerVisiblePages: viewerVisiblePagesSchema,
});
/**
 * Add existing user to current organization by email (Admin only)
 */
export const addMembershipByEmailSchema = z.object({
    email: emailSchema,
    role: userRoleSchema.default('Warehouse Staff'),
    locationIds: locationIdsSchema,
    viewerVisiblePages: viewerVisiblePagesSchema,
});
/**
 * Remove a user's membership from current organization (Admin only)
 */
export const removeMembershipSchema = z.object({
    userId: uuidSchema,
});
// Org invite acceptance (existing users)
export const acceptOrgInviteSchema = z.object({
    token: shortString.min(16, 'Invalid token'),
});
// Org invite management (admin)
export const resendOrgInviteSchema = z.object({
    inviteId: uuidSchema,
});
export const cancelOrgInviteSchema = z.object({
    inviteId: uuidSchema,
});
export const previewOrgInviteSchema = z.object({
    token: shortString.min(16, 'Invalid token'),
});
// Password reset request
export const passwordResetRequestSchema = z.object({
    email: emailSchema,
});
// Language preference
export const updateLanguagePreferenceSchema = z.object({
    language: z.enum(['en', 'zh']),
});
// Per-user UI display preferences (profiles.ui_preferences). ONE schema for
// read and write. `.passthrough()` on the top level AND on every section keeps
// keys this build does not know (written by a newer tab) instead of stripping
// them: an older tab that saves `orderQueue` must not drop a newer tab's
// `orderQueue.<newKey>`, and the server merge (stored || patch) replaces each
// sent section whole. Badge keys are an open string record so a key the
// frontend ships before the backend does not fail the save; the known keys
// stay typed on the frontend. The DB CHECK (object, at most 4 KB) is the hard
// limit; uiPreferencesPatchSchema rejects oversize patches before the DB.
export const uiPreferencesSchema = z
    .object({
    orderQueue: z
        .object({ badges: z.record(z.string().max(64), z.boolean()).optional() })
        .passthrough()
        .optional(),
})
    .passthrough();
/**
 * Max JSON.stringify length of a save patch. Below the DB CHECK's 4096 bytes
 * of jsonb text because jsonb text adds a space after every ':' and ',', so
 * the stored text is longer than JSON.stringify of the same value.
 */
export const UI_PREFERENCES_PATCH_MAX_CHARS = 3500;
/**
 * users.updateUiPreferences input: ONLY the top-level section(s) the client
 * changed (today `{ orderQueue }`). The server merges it into the stored value
 * at the top level, so sections this tab did not send are left untouched.
 */
export const uiPreferencesPatchSchema = uiPreferencesSchema.refine((patch) => JSON.stringify(patch).length <= UI_PREFERENCES_PATCH_MAX_CHARS, { message: `Display settings are too large to save (limit ${UI_PREFERENCES_PATCH_MAX_CHARS} characters).` });
/** Parse a stored ui_preferences value; `{}` for anything invalid. Never throws. */
export function parseUiPreferences(raw) {
    const parsed = uiPreferencesSchema.safeParse(raw);
    return parsed.success ? parsed.data : {};
}
